import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  catchError,
  defer,
  finalize,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import * as fromGenerated from '../../_generated';
// Concrete file, not the assistants barrel (see `chats.reducer.ts`).
import { AssistantsActions } from '../assistants/store/assistants.actions';
import { assistantsFeature } from '../assistants/store/assistants.reducer';
import { isDraft } from './chat-lifecycle.helper';
import { mintChatUuid } from './chat-uuid';
import { ChatActions } from './chats.actions';
import { chatsFeature, ChatsState } from './chats.reducer';

/** The chat a write path may now address: the uuid the create settled on. */
export interface EnsuredChat {
  uuid: string;
}

const isConflict = (error: unknown): boolean =>
  error instanceof HttpErrorResponse && error.status === 409;

/**
 * The one door through which a draft becomes a server chat (SB-3944 / KD-15):
 * the first message and the first file go through `ensureChatCreated` before
 * they send anything. A connector change on a draft creates nothing: the draft
 * keeps it in the store, and the create replays it (`replayConnectors`).
 *
 * Not exported from `store/index.ts`: it reads the assistants slice, and that
 * barrel is reached back from the selectors (an import cycle jest cannot
 * see). Import it by path.
 */
@Injectable({ providedIn: 'root' })
export class ChatCreationService {
  private readonly chatsService = inject(fromGenerated.ChatsService);
  private readonly store = inject<Store<ChatsState>>(Store);

  /** One shared create per draft; an entry lives until its create settles. */
  private readonly inFlight = new Map<string, Observable<EnsuredChat>>();
  /** Old uuid to the uuid a refused draft moved to (KD-18). */
  private readonly reassigned = new Map<string, string>();

  /**
   * A chat that already exists passes straight through. A draft is created
   * with `POST /chats`, once per uuid: two callers at the same time (two files
   * dropped together, a send typed during an upload) share one request. A 409
   * moves the draft to a new uuid and posts once more; any second failure goes
   * to the caller. The URL does not change on success (KD-30).
   */
  public ensureChatCreated(chatUuid: string): Observable<EnsuredChat> {
    const uuid = this.latestUuid(chatUuid);
    const pending = this.inFlight.get(uuid);
    if (pending) {
      return pending;
    }

    const chat = this.store.selectSignal(chatsFeature.selectChatByUuid(uuid))();
    if (!chat) {
      return throwError(() => new Error('chat not found in the store'));
    }
    if (!isDraft(chat)) {
      return of({ uuid });
    }

    const request: Observable<EnsuredChat> = defer(() =>
      this.createDraft(uuid),
    ).pipe(
      finalize(() => {
        for (const [key, value] of this.inFlight) {
          if (value === request) {
            this.inFlight.delete(key);
          }
        }
      }),
      // Not ref-counted: a caller cancelled by `switchMap` must not abort the
      // POST the other callers wait on.
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.inFlight.set(uuid, request);
    return request;
  }

  /**
   * Follows re-keys, so a failure is reported on the uuid the store now uses.
   */
  public latestUuid(chatUuid: string): string {
    let uuid = chatUuid;
    let next = this.reassigned.get(uuid);
    while (next !== undefined) {
      uuid = next;
      next = this.reassigned.get(uuid);
    }
    return uuid;
  }

  private createDraft(uuid: string): Observable<EnsuredChat> {
    return this.postWithSingleRetry(uuid, false).pipe(
      switchMap((created) => this.replayConnectors(created)),
      tap((created) => {
        // The server picks the skill of a default-chat draft, and a skill
        // designated a moment ago may not have reached the slice yet. Reload
        // it first: the conversation page waits for the load before it reads
        // a missing skill as an access loss.
        const assistantId = created.assistantId;
        if (
          assistantId &&
          !this.store.selectSignal(assistantsFeature.selectEntities)()[
            assistantId
          ]
        ) {
          this.store.dispatch(AssistantsActions.loadAssistants());
        }
        this.store.dispatch(
          ChatActions.createChatSuccess({ chatWithMessages: created }),
        );
      }),
      map((created) => ({ uuid: created.uuid })),
    );
  }

  private postWithSingleRetry(
    uuid: string,
    retried: boolean,
  ): Observable<fromGenerated.ChatWithMessagesAndOptionalAssistantView> {
    return defer(() => {
      const body = this.bodyFor(uuid);
      if (!body) {
        return throwError(() => new Error('chat not found in the store'));
      }
      return this.chatsService.chatsControllerCreateChatV1(body);
    }).pipe(
      catchError((error: unknown) => {
        if (retried || !isConflict(error)) {
          return throwError(() => error);
        }
        const toUuid = mintChatUuid();
        this.reassigned.set(uuid, toUuid);
        const shared = this.inFlight.get(uuid);
        if (shared) {
          this.inFlight.set(toUuid, shared);
        }
        this.store.dispatch(
          ChatActions.chatUuidReassigned({ fromUuid: uuid, toUuid }),
        );
        return this.postWithSingleRetry(toUuid, true);
      }),
    );
  }

  /** The skill comes from the draft, its locale from the skills slice. */
  private bodyFor(uuid: string): fromGenerated.CreateChatDto | null {
    const chat = this.store.selectSignal(chatsFeature.selectChatByUuid(uuid))();
    if (!chat) {
      return null;
    }
    const assistantUuid = chat.assistantId ?? undefined;
    const assistant = assistantUuid
      ? this.store.selectSignal(assistantsFeature.selectEntities)()[
          assistantUuid
        ]
      : undefined;
    return {
      uuid,
      ...(assistantUuid ? { assistantUuid } : {}),
      ...(assistant ? { assistantLocale: assistant.locale } : {}),
    };
  }

  /**
   * Connector exclusions toggled on the draft are local until now; send them
   * once the row exists. A failure fails the create, and the entity stays a
   * draft: the next attempt posts again (the API answers 200 for the same
   * user and skill) and replays the list.
   */
  private replayConnectors(
    created: fromGenerated.ChatWithMessagesAndOptionalAssistantView,
  ): Observable<fromGenerated.ChatWithMessagesAndOptionalAssistantView> {
    const disabledConnectorIds =
      this.store.selectSignal(chatsFeature.selectChatByUuid(created.uuid))()
        ?.disabledConnectorIds ?? [];
    if (disabledConnectorIds.length === 0) {
      return of(created);
    }
    return this.chatsService
      .chatsControllerUpdateChatConnectorsV1(created.uuid, {
        disabledConnectorIds,
      })
      .pipe(map(() => created));
  }
}
